import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { SnackBarService } from 'src/app/core/services/snackBar.service';
import { SatelliteLockService } from 'src/app/modules/satellite-lock/satellite-lock.service';
import { SatelliteLock } from 'src/app/core/interfaces/satelliteLock';
import { SatelliteLockType } from 'src/app/core/interfaces/satelliteLockType';
import { SatelliteLockStateDevice } from 'src/app/core/enums/satelliteLockStateDevice.enum';
import { Patterns } from 'src/app/core/resources/patterns';
import { Utils } from 'src/app/core/resources/utils';
import { FileService } from 'src/app/shared/files/file.service';
import { FileNamePipe } from 'src/app/core/pipe/fileName.pipe';
import { UploadTaskSnapshot } from '@angular/fire/storage/interfaces';
import { Subscription } from 'rxjs';
import { FormMessages } from 'src/app/core/messages/form-messages.enum';
import { Fmt } from 'src/app/core/messages/fmt';
import { ResourceMessages } from 'src/app/core/messages/resource-messages.enum';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { FileStorage } from 'src/app/core/interfaces/fileStorage';
import { PermissionRole } from 'src/app/core/resources/permission-role';
import { Permission } from 'src/app/core/resources/permission';

/**
 * Component for creating a new satellite lock
 * Handles form validation and submission to create a satellite lock
 */
@Component({
  selector: 'app-create-satellite-lock',
  templateUrl: './create-satellite-lock.component.html',
  styleUrls: ['./create-satellite-lock.component.scss']
})
export class CreateSatelliteLockComponent implements OnInit {
  @ViewChild('inputImage') inputImage: ElementRef;

  satelliteLockForm: FormGroup;
  visualImage: FormControl = new FormControl('');
  imageResource: File | { url: string, type: string } | null = null;
  fileResource: File | null = null;
  stateDeviceSubscription: Subscription;
  errorLoadImage: boolean = false;
  loadingImage: boolean = false;
  typeAutocompleteOptions = {
    title: 'Tipo de dispositivo',
    appearance: 'outline',
    initialValue: ''
  };
  typeValidate: string = '';
  readonly SatelliteLockStateDevice = SatelliteLockStateDevice;
  readonly stateDeviceOptions = [
    { value: SatelliteLockStateDevice.ACTIVE, label: 'Activo' },
    { value: SatelliteLockStateDevice.INACTIVE, label: 'Inactivo' },
    { value: SatelliteLockStateDevice.IN_MAINTENANCE, label: 'En mantenimiento' }
  ];
  permission = Permission;
  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly router: Router,
    private readonly satelliteLockService: SatelliteLockService,
    private readonly spinner: NgxSpinnerService,
    private readonly snackBarService: SnackBarService,
    public readonly patterns: Patterns,
    private readonly utils: Utils,
    private readonly fileService: FileService,
    private readonly fileNamePipe: FileNamePipe,
    permissionRole: PermissionRole
  ) {
    if (!permissionRole.hasPermission(this.permission.administration.module, this.permission.administration.createSatelliteLock)) {
      this.goBack();
      return;
    }
    this.initForm();
  }
  ngOnInit(): void {
    this.setupStateDeviceListener();
  }
  private initForm(): void {
    this.satelliteLockForm = this.formBuilder.group({
      id: ['', [Validators.required, Validators.pattern(this.patterns.SATELLITE_LOCK_ID_FIRMWARE.source)]],
      type: ['', Validators.required],
      imei: ['', [Validators.required]],
      consecutive: ['', [Validators.required]],
      firmwareVersion: ['', [Validators.pattern(this.patterns.SATELLITE_LOCK_ID_FIRMWARE.source)]],
      state: [true],
      stateDevice: [SatelliteLockStateDevice.ACTIVE, Validators.required],
      description: [{ value: '', disabled: true }, []],
      image: ['']
    });
  }

  private setupStateDeviceListener(): void {
    const stateDeviceControl = this.satelliteLockForm.get('stateDevice');
    const descriptionControl = this.satelliteLockForm.get('description');

    if (stateDeviceControl && descriptionControl) {
      this.stateDeviceSubscription = stateDeviceControl.valueChanges.subscribe((newState: SatelliteLockStateDevice) => {
        this.updateDescriptionFieldState(newState);
      });
      this.updateDescriptionFieldState(stateDeviceControl.value);
    }
  }


  private updateDescriptionFieldState(stateDevice: SatelliteLockStateDevice): void {
    const descriptionControl = this.satelliteLockForm.get('description');
    if (!descriptionControl) return;
    const isActive = stateDevice === SatelliteLockStateDevice.ACTIVE;

    if (isActive) {
      descriptionControl.disable();
      descriptionControl.clearValidators();
      descriptionControl.setValidators([]);
      descriptionControl.setValue('');
    } else {
      descriptionControl.enable();
      descriptionControl.setValidators([Validators.required]);
    }

    descriptionControl.updateValueAndValidity();
  }


  private checkCustomValidations(): boolean {
    this.satelliteLockForm.markAllAsTouched();

    const validations = [
      { control: 'imei', label: 'IMEI del dispositivo' },
      { control: 'consecutive', label: 'consecutivo' },
      { control: 'type', label: 'tipo de dispositivo' },
      { control: 'id', label: 'ID del dispositivo' },
      { control: 'stateDevice', label: 'estado del dispositivo' },
      { control: 'description', label: 'descripción' },
      { control: 'firmwareVersion', label: 'versión de firmware' },
    ];

    for (const validation of validations) {
      const control = this.satelliteLockForm.get(validation.control);
      if (this.utils.errorMessagesCustomized(control, validation.label))
        return true;
    }
    return false;
  }

  async onSubmit(): Promise<void> {
    ['id', 'firmwareVersion', 'description'].forEach(controlName => {
      const control = this.satelliteLockForm.get(controlName);
      if (control) control.setValue(control.value.trim());
    });
    if (this.checkCustomValidations()) return;
    this.spinner.show();
    if (this.fileResource) {
      const pathImage = this.satelliteLockService.pathImageSatelliteLock.replace('%s', this.satelliteLockForm.get('imei').value);
      const uploadImage = await this.uploadImageFile(`${pathImage}/${this.fileResource.name}`, this.fileResource);
      if (!uploadImage) {
        this.spinner.hide();
        return this.snackBarService.openSnackBar(Fmt.string(ResourceMessages.ERROR_UPLOAD, 'la imagen'), undefined, 'error');
      }
      this.satelliteLockForm.get('image').setValue(this.fileResource.name);
    }
    this.createSatelliteLock(this.satelliteLockForm.getRawValue());
  }

  private createSatelliteLock(satelliteLock: SatelliteLock): void {
    this.satelliteLockService.createSatelliteLock(satelliteLock).subscribe({
      next: (createdLock: SatelliteLock) => {
        this.spinner.hide();
        this.snackBarService.openSnackBar(
          'Candado satelital creado exitosamente',
          undefined,
          'success'
        );

        const imei = createdLock.imei;
        if (imei) {
          this.router.navigate(['administration/satellite-lock/detail', imei]);
        }
      },
      error: (error) => {

        this.spinner.hide();
        let errorMessage = 'Error al crear el candado satelital';
        if (error && error.error && typeof error.error === 'string') errorMessage = error.error;
        this.snackBarService.openSnackBar(
          errorMessage,
          undefined,
          'error'
        );
      }
    });
  }

  clearImage(): void {
    this.visualImage.setValue('');
    this.imageResource = null;
    this.errorLoadImage = false;
    this.loadingImage = false;
    this.satelliteLockForm.get('image').setValue('');
    if (this.inputImage && this.inputImage.nativeElement) {
      this.inputImage.nativeElement.value = '';
    }
  }

  goBack(): void {
    this.router.navigate(['administration/satellite-lock/list']);
  }

  setInstanceFileStorage(e: Event): void {
    if (!e.target || !e.target['files'] || !e.target['files'].length) return;
    const file = e.target['files'][0];
    if (!this.utils.isImage(file)) return this.snackBarService.openSnackBar('El archivo no es una imagen', undefined, 'error');
    const splittedNameFile = file.name.split('.');
    const formatFile = splittedNameFile[splittedNameFile.length - 1];
    const dateInMs = new Date().getTime();
    const fileName = `satelliteLockImage_${dateInMs}.${formatFile}`;
    this.fileResource = new File([file], fileName, {
      type: file.type
    });

    this.visualImage.setValue(this.fileNamePipe.transform(fileName) || fileName);
    this.imageResource = file;
    this.errorLoadImage = false;
  }


  private async uploadImageFile(filePath: string, imageFile: File): Promise<boolean> {
    try {
      const uploadResult = await this.fileService.uploadFile({
        path: filePath,
        imageFile: imageFile,
        key: 'image'
      });
      return uploadResult.status;
    } catch (error) {
      return false;
    }
  }


  openWindowFile(): void {
    if (this.inputImage && this.inputImage.nativeElement) this.inputImage.nativeElement.click();
  }

  hasError(controlName: string): boolean {
    const control = this.satelliteLockForm.get(controlName);
    return !!(control && control.errors && control.touched);
  }

  getErrorMessage(controlName: string): string {
    const control = this.satelliteLockForm.get(controlName);
    if (control && control.errors && control.touched) {
      if (control.errors['required']) return 'Este campo es requerido';
      if (control.errors['pattern']) return 'Solo se permiten letras, números y los caracteres . - _ / #';
    }
    return '';
  }

  ngOnDestroy(): void {
    if (this.stateDeviceSubscription) this.stateDeviceSubscription.unsubscribe();
  }
}
